import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiShoppingBag, FiMail, FiInstagram, FiFacebook, FiTwitter, FiMessageCircle, FiPhone,
} from 'react-icons/fi';
import './Footer.css';

// Social / messaging links are env-driven so the same build works across
// environments without code edits. Set in Vercel:
//   REACT_APP_INSTAGRAM_URL, REACT_APP_FACEBOOK_URL, REACT_APP_TWITTER_URL,
//   REACT_APP_WHATSAPP_NUMBER (digits-only, with country code),
//   REACT_APP_VIBER_NUMBER (digits-only, with country code),
//   REACT_APP_SUPPORT_EMAIL
// Empty / missing values are hidden — no broken `#` links.
const env = (k) => (process.env[k] || '').trim();

// Canonical brand URLs — used when the matching env var is not set. We
// intentionally strip share/tracking params (utm_source, igsh, rdid,
// share_url, fbclid, etc.) so every shopper that opens the footer does NOT
// carry the install-time referral identifiers of whoever first copied the
// link. Keeps analytics clean on both ends and avoids accidentally
// attributing organic traffic to a single QR campaign forever.
const DEFAULT_INSTAGRAM = 'https://www.instagram.com/shopyalley/';
const DEFAULT_FACEBOOK  = 'https://www.facebook.com/people/shopyalley/61590475100251/';

const SOCIALS = [
  { href: env('REACT_APP_INSTAGRAM_URL') || DEFAULT_INSTAGRAM, label: 'Instagram', Icon: FiInstagram },
  { href: env('REACT_APP_FACEBOOK_URL')  || DEFAULT_FACEBOOK,  label: 'Facebook',  Icon: FiFacebook },
  { href: env('REACT_APP_TWITTER_URL'),                         label: 'Twitter',   Icon: FiTwitter },
].filter((s) => s.href);

const whatsappNumber = env('REACT_APP_WHATSAPP_NUMBER').replace(/\D/g, '');
const viberNumber = env('REACT_APP_VIBER_NUMBER').replace(/\D/g, '');
const supportEmail = env('REACT_APP_SUPPORT_EMAIL') || 'support@shopyalley.com';

const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer__grid">
        <div className="footer__brand">
          <Link to="/" className="footer__logo">
            <FiShoppingBag /> Shopyalley
          </Link>
          <p>Your one-stop destination for premium clothes, footwear and exquisite jewellery. Shop the latest trends with confidence.</p>
          {SOCIALS.length > 0 && (
            <div className="footer__social">
              {SOCIALS.map(({ href, label, Icon }) => (
                <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer">
                  <Icon />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="footer__links">
          <h4>Shop</h4>
          <Link to="/shop">Shop All</Link>
          <Link to="/shop/clothes">Clothes</Link>
          <Link to="/shop/jewellery">Jewellery</Link>
          <Link to="/shop/footwear">Footwear</Link>
          <Link to="/shop?newArrivals=true">New Arrivals</Link>
        </div>

        <div className="footer__links">
          <h4>Customer Service</h4>
          <Link to="/orders">Track Order</Link>
          <Link to="/profile">My Account</Link>
          <Link to="/wishlist">Wishlist</Link>
          <Link to="/privacy">Privacy Policy</Link>
        </div>

        <div className="footer__contact">
          <h4>Contact Us</h4>
          <p>
            <FiMail aria-hidden="true" />{' '}
            <a href={`mailto:${supportEmail}`} style={{ color: 'inherit' }}>{supportEmail}</a>
          </p>

          {whatsappNumber && (
            <p>
              <FiMessageCircle aria-hidden="true" />{' '}
              <a
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit' }}
              >
                WhatsApp chat
              </a>
            </p>
          )}

          {viberNumber && (
            <p>
              <FiPhone aria-hidden="true" />{' '}
              <a href={`viber://chat?number=%2B${viberNumber}`} style={{ color: 'inherit' }}>
                Viber chat
              </a>
            </p>
          )}

          <Link to="/contact" className="footer__contact-link">Send us a message &rarr;</Link>
        </div>
      </div>

      <div className="footer__bottom">
        <p>&copy; {new Date().getFullYear()} Shopyalley. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default Footer;
